import filter from 'lodash/filter'
import find from 'lodash/find'
import each from 'lodash/each'
import cloneDeep from 'lodash/cloneDeep'
import reject from 'lodash/reject'
import isEmpty from 'lodash/isEmpty'
import { useDispatch, useSelector } from 'react-redux'
import { useState, useRef, useEffect } from 'react'
import useStep from './useStep'
import {
  setGuestsSplitiing,
  setRoomParams,
  setTotalPrice,
} from '../../../actions/checkout-la-actions'

export const generateRoomParams = (guestsSplitting) => {
  const { totalPrice, roomParams } = guestsSplitting.reduce(
    (acc, val) => {
      acc.totalPrice += val.totalCabinPrice
      acc.roomParams.divers += val.divers
      acc.roomParams.nonDivers += val.nonDivers
      acc.roomParams.totalGuests += val.totalGuests
      val.cart.forEach((cartObject) => {
        const data = cartObject.reduce(
          (a, room) => {
            a[room.isDiver ? 'divers' : 'nonDivers'] += room.qty
            return a
          },
          {
            roomId: val.id,
            divers: 0,
            nonDivers: 0,
            students: 0,
          }
        )
        acc.roomParams.details.push(data)
      })

      return acc
    },
    {
      totalPrice: 0,
      roomParams: {
        divers: 0,
        nonDivers: 0,
        totalGuests: 0,
        details: [],
      },
    }
  )

  return {
    totalPrice,
    roomParams,
  }
}

export default function useGuestSplitting() {
  const cabinsPricingObject = useRef([])
  const dispatch = useDispatch()
  const [selectGuestPopup, setSelectGuestPopup] = useState({})
  const { setStep } = useStep()
  const { cabins } = useSelector((state) => ({
    cabins: state.selectedCabins,
  }))
  const setSpots = ({ cabin, sharedForDiver, preferSingle, maxOccupancy }) => {
    let pricing = []
    let prices = filter(cabin.pricesToShow, 'qty')
    let cart = []
    let isDiverUser = true
    if(sharedForDiver) {
      let nonDiversToGo = cabin.nonDiversToGo
      cabin.nonDiversToGo = cabin.diversToGo
      cabin.diversToGo = nonDiversToGo
      isDiverUser = false
    }
    const guests = {
      divers: cabin.diversToGo,
      nonDivers: cabin.nonDiversToGo,
    }
    if(cabin.totalGuests <= maxOccupancy) {
      let groupedCart = []
      prices.forEach((priceObject) => {
        let qty = cabin[priceObject.isDiver ? 'diversToGo' : 'nonDiversToGo']
        let product = find(cabin.prices, {
          numberOfInstances: cabin.totalGuests,
          isDiver: priceObject.isDiver,
          isShared:
            cabin.totalGuests > 1 ? false : cabin.isShared && !preferSingle,
        })
        product.qty = qty
        pricing.push(product)
        groupedCart.push(product)
      })
      cart.push(groupedCart)
    } else {
      let cabinsNumber = new Array(Math.ceil(cabin.totalGuests / maxOccupancy))
      let oneToShared = cabin.totalGuests % maxOccupancy === 1
      each(cabinsNumber, () => {
        let groupedCart = []
        if(cabin.diversToGo >= maxOccupancy) {
          let product = cloneDeep(
            find(cabin.prices, {
              numberOfInstances: maxOccupancy,
              isDiver: isDiverUser,
            })
          )
          product.qty = maxOccupancy
          pricing.push(product)
          groupedCart.push(product)
          cabin.diversToGo -= maxOccupancy
        } else if(
          (cabin.nonDiversToGo >= maxOccupancy && !oneToShared) ||
          (cabin.nonDiversToGo > maxOccupancy && oneToShared)
        ) {
          let product = cloneDeep(
            find(cabin.prices, {
              numberOfInstances: maxOccupancy,
              isDiver: !isDiverUser,
            })
          )

          product.qty = maxOccupancy
          pricing.push(product)
          cabin.nonDiversToGo -= maxOccupancy
          groupedCart.push(product)
        } else if(cabin.nonDiversToGo + cabin.diversToGo >= maxOccupancy) {
          let diverProduct = cloneDeep(
            find(cabin.prices, {
              numberOfInstances: maxOccupancy,
              isDiver: isDiverUser,
            })
          )
          diverProduct.qty = cabin.diversToGo
          pricing.push(diverProduct)

          let nonDiverProduct = cloneDeep(
            find(cabin.prices, {
              numberOfInstances: maxOccupancy,
              isDiver: !isDiverUser,
            })
          )
          nonDiverProduct.qty = maxOccupancy - cabin.diversToGo
          pricing.push(nonDiverProduct)
          groupedCart.push(diverProduct)
          groupedCart.push(nonDiverProduct)
          cabin.nonDiversToGo -= nonDiverProduct.qty
          cabin.diversToGo = 0
        } else if(cabin.diversToGo + cabin.nonDiversToGo < maxOccupancy) {
          let numberOfInstances = cabin.diversToGo + cabin.nonDiversToGo
          if(cabin.diversToGo) {
            let diverProduct = cloneDeep(
              find(cabin.prices, {
                numberOfInstances,
                isDiver: isDiverUser,
                isShared:
                  cabin.isShared && !preferSingle && cabin.diversToGo === 1,
              })
            )
            diverProduct.qty = cabin.diversToGo
            pricing.push(diverProduct)
            cabin.diversToGo = 0
            groupedCart.push(diverProduct)
          }
          if(cabin.nonDiversToGo) {
            let nonDiverProduct = cloneDeep(
              find(cabin.prices, {
                numberOfInstances,
                isDiver: !isDiverUser,
                isShared:
                  cabin.isShared && !preferSingle && cabin.nonDiversToGo === 1,
              })
            )
            nonDiverProduct.qty = cabin.nonDiversToGo
            pricing.push(nonDiverProduct)
            cabin.nonDiversToGo = 0
            groupedCart.push(nonDiverProduct)
          }
        }
        cart.push(groupedCart)
      })
    }
    const checkoutObject = {
      title: cabin.roomCategory.title,
      id: cabin.roomCategory.id,
      totalGuests: cabin.totalGuests,
      ...guests,
      totalCabinPrice: pricing.reduce((acc, val) => {
        acc += val.priceNet * val.qty
        return acc
      }, 0),
      cart,
      pricing,
    }

    let selectedCabins = reject(cabinsPricingObject.current, {
      id: cabin.roomCategory.id,
    })
    selectedCabins.push(checkoutObject)
    cabinsPricingObject.current = selectedCabins
  }

  const setData = () => {
    const { roomParams, totalPrice } = generateRoomParams(
      cabinsPricingObject.current
    )
    dispatch(setGuestsSplitiing(cabinsPricingObject.current))
    dispatch(setRoomParams(roomParams))
    dispatch(setTotalPrice(totalPrice))
    setStep(3)
  }
  useEffect(() => {
    if(!isEmpty(selectGuestPopup)) {
      let allPopupsFinished = true
      each(selectGuestPopup, function(value) {
        if(value) {
          allPopupsFinished = false
        }
      })

      if(allPopupsFinished) {
        setData()
      }
    }
  }, [selectGuestPopup])

  const splitGuestsToCabins = () => {
    cabinsPricingObject.current = []
    let cabinsToShowPopup = {}
    let guests = {
      diversNumber: 0,
      nonDiversNumber: 0,
      totalGuests: 0,
    }
    cabins.forEach((cabin) => {
      guests.diversNumber += cabin.diversToGo
      guests.nonDiversNumber += cabin.nonDiversToGo
      guests.totalGuests += cabin.totalGuests

      let maxOccupancy = cabin.roomCategory.maxOccupancy
      if(
        cabin.diversToGo >= 1 &&
        cabin.nonDiversToGo >= 1 &&
        cabin.totalGuests % maxOccupancy === 1
      ) {
        const cabinObject = cloneDeep(cabin)
        cabinsToShowPopup[cabin.roomCategory.id] = {
          cabin: cabinObject,
          callback: (isDiver) => {
            setSpots({
              sharedForDiver: isDiver,
              preferSingle: false,
              cabin: cabinObject,
              maxOccupancy: cabin.roomCategory.maxOccupancy,
            })
            setSelectGuestPopup((val) => ({
              ...val,
              [cabin.roomCategory.id]: false,
            }))
          },
        }
      } else {
        setSpots({
          preferSingle: false,
          cabin,
          maxOccupancy,
        })
      }
    })
    if(!isEmpty(cabinsToShowPopup)) {
      setSelectGuestPopup(cabinsToShowPopup)
    } else {
      setData()
    }
  }

  return {
    cabinsPricingObject: cabinsPricingObject.current,
    selectGuestPopup,
    splitGuestsToCabins,
  }
}
